import { z } from 'zod';
import { AssetAttachmentKindSchema } from './asset-attachment.js';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in ar.ts /
// ap.ts / loan.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Default-fallback cap. The authoritative threshold is the per-company
// CompanyCapitalizationPolicy resolved server-side (resolveCapitalizationCap);
// this constant is only the fallback when no policy row applies. Items at or
// above the resolved cap must be capitalised under the company's capitalization
// / materiality policy (TFRS for NPAEs) rather than expensed in full.
// This is NOT a DTO bound — see LVA_COST_SANITY_CEILING below.
export const LVA_COST_CAP = 50_000;
// Soft sanity ceiling for the CreateLvaItem DTO only. The real per-company cap
// can be configured ABOVE LVA_COST_CAP, so the wire bound must not pin to
// LVA_COST_CAP or it would block a company whose policy threshold is higher
// from ever creating those items. This absolute ceiling only rejects obviously
// fat-fingered amounts; the binding capitalization gate is resolveCapitalizationCap.
export const LVA_COST_SANITY_CEILING = 100_000_000;
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const LvaStatusSchema = z.enum(['IN_USE', 'DISPOSED', 'LOST']);
export const LvaEventTypeSchema = z.enum([
    'TRANSFER',
    'REASSIGN_CUSTODIAN',
    'MARK_DAMAGED',
    'MARK_LOST',
    'DISPOSE',
]);
export const LvaConditionSchema = z.enum(['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED']);
export const LvaRiskTierSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
// ============================================================
// LvaCategory — per-company master (mirrors AssetCategory)
// ============================================================
export const LvaCategorySchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1).max(64),
    nameTh: z.string().min(1),
    toneColor: z.string().min(1),
    defaultExpenseCode: z.string().min(1).nullable().optional(),
    riskTier: LvaRiskTierSchema.default('LOW'),
    isActive: z.boolean().default(true),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// LvaItem — base row
// ============================================================
export const LvaItemSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    categoryName: z.string().min(1),
    categoryId: z.uuid().nullable().optional(),
    custodianEmployeeId: z.uuid().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    acquiredDate: z.coerce.date(),
    cost: DecimalLike,
    location: z.string(),
    assignedTo: z.string(),
    serialNumber: z.string().nullable().optional(),
    warrantyExpiry: z.coerce.date().nullable().optional(),
    condition: LvaConditionSchema.default('GOOD'),
    sourceDocRef: z.string().nullable().optional(),
    quantity: z.number().int().positive().default(1),
    unit: z.string().nullable().optional(),
    status: LvaStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    disposedDate: z.coerce.date().nullable().optional(),
    disposedReason: z.string().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    // QR-label print tracking (Phase B). Set when a label sheet including this
    // item is generated with markPrinted; qrPrintedBy carries the actor label.
    qrPrintedAt: z.coerce.date().nullable().optional(),
    qrPrintedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds derived display names for table rendering.
export const LvaItemListRowSchema = LvaItemSchema.extend({
    branchName: z.string().nullable().optional(),
    custodianName: z.string().nullable().optional(),
    categoryToneColor: z.string().nullable().optional(),
});
// ============================================================
// QR label printing (Phase B)
// Layout = labels per A4 sheet (8 / 24 / 65 Avery-style grids).
// A request targets either an explicit set of itemIds or a server-resolved
// filter; webOrigin (when present) seeds the deep-link base in each QR.
// markPrinted stamps qrPrintedAt/qrPrintedBy on the included IN_USE items.
// ============================================================
export const LvaLabelLayoutSchema = z.enum(['8', '24', '65']);
export const LvaLabelRequestSchema = z
    .object({
    itemIds: z.array(z.uuid()).optional(),
    filter: z.enum(['all', 'unprinted', 'thisMonth']).optional(),
    layout: LvaLabelLayoutSchema,
    webOrigin: z.string().url().optional(),
    markPrinted: z.boolean().default(true),
    // Optional built-in label-printer preset (Phase E). When set, the server
    // maps presetId → its layout + any margin/gutter overrides. The preset's
    // layout takes precedence over the explicit `layout` field, so the client
    // need only send a valid layout (required by the schema) plus the presetId.
    presetId: z.string().min(1).optional(),
})
    .refine((v) => (v.itemIds != null && v.itemIds.length > 0) || v.filter != null, {
    message: 'Provide a non-empty itemIds selection or a filter',
    path: ['itemIds'],
});
// ============================================================
// Label-printer / material presets (Phase E — built-in, no table).
//
// Each preset pairs a label LAYOUT (8/24/65 — drives the on-page grid) with
// printer/material metadata for the user (printerModel/materialType) and
// OPTIONAL margin/gutter overrides (points) threaded into renderLabelSheetPdf.
// When an override is omitted the renderer keeps its built-in PAGE_MARGIN/
// GUTTER, so a preset that tweaks nothing renders byte-for-byte like no preset.
// These are constants (a single source of truth shared by server + client);
// a configurable preset master is intentionally out of scope for the polish
// phase (see design D5).
// ============================================================
export const LvaLabelPresetSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    layout: LvaLabelLayoutSchema,
    printerModel: z.string().min(1).optional(),
    materialType: z.string().min(1).optional(),
    marginTop: z.number().nonnegative().optional(),
    marginLeft: z.number().nonnegative().optional(),
    gutter: z.number().nonnegative().optional(),
});
// Built-in presets: an Avery-style sheet per layout + a Brother QL DK-roll
// example. Margin/gutter are left unset on the Avery sheets (they match the
// renderer defaults); the Brother roll nudges the margins for the narrower
// continuous label. Readonly so callers can't mutate the shared list.
export const LVA_LABEL_PRESETS = [
    {
        id: 'avery-l7165-8',
        name: 'Avery L7165 — 8 ดวง/หน้า (99×67mm)',
        layout: '8',
        printerModel: 'เครื่องพิมพ์เลเซอร์ A4',
        materialType: 'Avery L7165 (กระดาษสติกเกอร์ A4)',
    },
    {
        id: 'avery-l7159-24',
        name: 'Avery L7159 — 24 ดวง/หน้า (64×34mm)',
        layout: '24',
        printerModel: 'เครื่องพิมพ์เลเซอร์ A4',
        materialType: 'Avery L7159 (กระดาษสติกเกอร์ A4)',
    },
    {
        id: 'avery-l7651-65',
        name: 'Avery L7651 — 65 ดวง/หน้า (38×21mm)',
        layout: '65',
        printerModel: 'เครื่องพิมพ์เลเซอร์ A4',
        materialType: 'Avery L7651 (กระดาษสติกเกอร์ A4)',
    },
    {
        id: 'brother-ql-dk-roll-24',
        name: 'Brother QL-800 — DK-roll (24 ดวง/หน้า)',
        layout: '24',
        printerModel: 'Brother QL-800',
        materialType: 'Brother DK-roll (สติกเกอร์ม้วนต่อเนื่อง)',
        marginTop: 24,
        marginLeft: 24,
        gutter: 4,
    },
];
// ============================================================
// CreateLvaItem — POST body
// code is server-generated (LVA-YYYY-NNNNN). cashAccountCode controls the
// credit side of the auto-JV; when omitted the service resolves CASH_PETTY
// per-company. categoryName is validated as non-empty free text (legacy
// fallback); categoryId is the FK to the LvaCategory master when set.
// ============================================================
export const CreateLvaItemSchema = z.object({
    name: z.string().min(1),
    categoryName: z.string().min(1),
    categoryId: z.uuid().optional(),
    custodianEmployeeId: z.uuid().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    acquiredDate: z.iso.date(),
    // Soft sanity ceiling only — NOT the capitalization cap. The authoritative
    // per-company threshold is enforced server-side (resolveCapitalizationCap),
    // which may be configured above LVA_COST_CAP. Pinning the wire bound to the
    // cap would defeat raising the per-company threshold, so we only reject
    // obviously fat-fingered amounts here.
    cost: z.coerce
        .number()
        .positive()
        .lt(LVA_COST_SANITY_CEILING, `LVA cost must be < ${LVA_COST_SANITY_CEILING} (sanity limit); the binding capitalization/materiality cap (TFRS for NPAEs) is enforced server-side per company`),
    location: z.string().min(1),
    assignedTo: z.string().min(1),
    serialNumber: z.string().min(1).optional(),
    warrantyExpiry: z.iso.date().optional(),
    condition: LvaConditionSchema.default('GOOD'),
    sourceDocRef: z.string().min(1).optional(),
    quantity: z.coerce.number().int().positive().default(1),
    unit: z.string().min(1).optional(),
    // Omit to credit the per-company CASH_PETTY account; set explicitly (e.g. an
    // AP/credit account) to override the credit side of the auto-JV.
    cashAccountCode: z.string().min(1).optional(),
});
// ============================================================
// UpdateLvaItem — PATCH :id body (.strict)
// Editable master fields only. Immutable (code, companyId, journalEntryId,
// cost, acquiredDate) are rejected by .strict() — cost/date drove the posted JV.
// Field edits are not lifecycle events (no movement row written).
// ============================================================
export const UpdateLvaItemSchema = z
    .object({
    name: z.string().min(1),
    categoryId: z.uuid(),
    serialNumber: z.string().min(1).nullable(),
    warrantyExpiry: z.iso.date().nullable(),
    condition: LvaConditionSchema,
    sourceDocRef: z.string().min(1).nullable(),
    quantity: z.coerce.number().int().positive(),
    unit: z.string().min(1).nullable(),
    location: z.string().min(1),
})
    .partial()
    .strict();
// ============================================================
// Lifecycle body schemas
// ============================================================
// transfer — at least one of branch / department must change.
export const TransferLvaItemSchema = z
    .object({
    date: z.iso.date(),
    toBranchId: z.uuid().nullable().optional(),
    toDepartmentId: z.uuid().nullable().optional(),
    reason: z.string().min(1),
})
    .refine((v) => v.toBranchId != null || v.toDepartmentId != null, {
    message: 'A transfer must change at least the branch or the department',
    path: ['toBranchId'],
});
// reassign-custodian — employee FK or free-text fallback.
export const ReassignCustodianSchema = z.object({
    date: z.iso.date(),
    toCustodianEmployeeId: z.uuid().nullable().optional(),
    toCustodianText: z.string().min(1).nullable().optional(),
    reason: z.string().min(1),
});
// mark-damaged — actor only, no approver; status stays IN_USE.
export const MarkDamagedLvaItemSchema = z.object({
    date: z.iso.date(),
    condition: LvaConditionSchema.default('DAMAGED'),
    reason: z.string().min(1),
});
// mark-lost — reason + approver required (lightweight SoD enforced server-side).
// approvedBy carries the approving EMPLOYEE's id (single-namespace identity so
// the server can compare it against the submitter's resolved employeeId). A
// human-readable approver label is resolved server-side for the audit log.
export const MarkLostLvaItemSchema = z.object({
    date: z.iso.date(),
    reason: z.string().min(1),
    approvedBy: z.uuid(),
});
// ============================================================
// DisposeLvaItem — POST :id/dispose body
// No JV — the asset was already expensed at acquisition. Pure status flip.
// Upgraded: reason now required + approvedBy added (SoD enforced server-side).
// approvedBy carries the approving EMPLOYEE's id (single-namespace identity, as
// for mark-lost); the readable approver label is resolved server-side.
// ============================================================
export const DisposeLvaItemSchema = z.object({
    date: z.iso.date(),
    reason: z.string().min(1),
    approvedBy: z.uuid(),
});
// ============================================================
// LvaMovement — append-only lifecycle log (mirrors ApprovalEvent)
// Typed from/to columns for branch / department / custodian so the UI can join
// names and filter; JSON only for spillover detail.
// ============================================================
export const LvaMovementSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    lvaItemId: z.uuid(),
    code: z.string().min(1),
    eventType: LvaEventTypeSchema,
    eventDate: z.coerce.date(),
    submittedBy: z.string().min(1),
    reason: z.string().nullable().optional(),
    fromBranchId: z.uuid().nullable().optional(),
    toBranchId: z.uuid().nullable().optional(),
    fromDepartmentId: z.uuid().nullable().optional(),
    toDepartmentId: z.uuid().nullable().optional(),
    fromCustodianEmpId: z.uuid().nullable().optional(),
    toCustodianEmpId: z.uuid().nullable().optional(),
    fromCustodianText: z.string().nullable().optional(),
    toCustodianText: z.string().nullable().optional(),
    conditionAfter: LvaConditionSchema.nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    attachmentId: z.uuid().nullable().optional(),
    detail: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.coerce.date(),
});
// List-row shape — adds resolved display names + the parent item's code.
export const LvaMovementListRowSchema = LvaMovementSchema.extend({
    lvaCode: z.string().nullable().optional(),
    fromBranchName: z.string().nullable().optional(),
    toBranchName: z.string().nullable().optional(),
    fromCustodianName: z.string().nullable().optional(),
    toCustodianName: z.string().nullable().optional(),
});
// ============================================================
// CompanyCapitalizationPolicy — per-company materiality threshold row.
// ============================================================
export const CompanyCapitalizationPolicySchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    threshold: DecimalLike,
    frameworkRef: z.string().min(1).default('TFRS for NPAEs'),
    effectiveDate: z.coerce.date(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// LvaAttachment — image / document metadata for an LvaItem.
// Binary lives in MinIO under lva-items/<id>/...; this row holds the storage
// key + display fields. Reuses the AssetAttachmentKind enum (image/* => IMAGE).
// ============================================================
export const LvaAttachmentSchema = z.object({
    id: z.uuid(),
    lvaItemId: z.uuid(),
    kind: AssetAttachmentKindSchema,
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    storageKey: z.string(),
    caption: z.string().nullable().optional(),
    uploadedBy: z.string().nullable().optional(),
    uploadedAt: z.coerce.date(),
});
//# sourceMappingURL=lva.js.map